import { makeAutoObservable } from "mobx";
import { performApiRequest } from "@/utils/api";
import { EntityRepository } from "@/app_store/stream/entity_repository";


export interface AccountSettings {
    ui_split_sizes: number[];
    ui_agent_sizes: number[];
    ui_agent_open: boolean;
    theme_desktop: string;
    theme_mobile: string;
    language: string;
    max_open_tabs_desktop: number;
    max_open_tabs_mobile: number;
    editor_font_size_desktop: number;
    editor_font_size_mobile: number;
    deploy_log_font_size_desktop: number;
    deploy_log_font_size_mobile: number;
    claude_backend: string;
    claude_model: string;
    claude_effort: string;
}

export interface Account {
    id: string;
    email: string;
    full_name: string;
    settings: AccountSettings;
    updated_at: number; // float Unix timestamp
}


export class AccountStore {
    root: any;
    token: string | null = null;
    account: Account | null = null;
    repository = new EntityRepository<Account>("id");

    get isAuthenticated() {
        return this.token !== null && this.account !== null;
    }

    get settings(): AccountSettings | null {
        const entity = this.account ? this.repository.get(this.account.id) : null;
        return entity?.value.settings ?? null;
    }

    constructor(root: any) {
        this.root = root;
        makeAutoObservable(this, { repository: false });

        setTimeout(() => {
            const token = localStorage.getItem("token");
            const accountRaw = localStorage.getItem("account");
            if (token && accountRaw) {
                try {
                    this.account = JSON.parse(accountRaw);
                    this.setToken(token);
                } catch {
                    localStorage.removeItem("token");
                    localStorage.removeItem("account");
                }
            }
        }, 0);
    }

    saveSettings(partial: Partial<AccountSettings>) {
        if (!this.account) return;
        const entity = this.repository.get(this.account.id);
        if (!entity) return;
        const current = entity.value.settings || { ui_split_sizes: [], ui_agent_sizes: [] };
        entity.update({ settings: { ...current, ...partial } });
    }

    setToken(token: string | null) {
        if (token !== this.token) {
            if (this.token) {
                this.root.closeStream();
            }
            this.token = token;
            if (token) {
                this.root.openStream();
            }
        }
    }

    private async _authRequest(endpoint: string, body: object): Promise<void> {
        const data = await performApiRequest(
            `${this.root.apiUrl}/api/${endpoint}`,
            "POST",
            body
        );
        this.account = { id: data.id, email: data.email, full_name: data.full_name, settings: data.settings || { ui_split_sizes: [], ui_agent_sizes: [], ui_agent_open: false, theme_desktop: "darcula", theme_mobile: "darcula", language: "en", max_open_tabs_desktop: 10, max_open_tabs_mobile: 2, editor_font_size_desktop: 13, editor_font_size_mobile: 13, deploy_log_font_size_desktop: 13, deploy_log_font_size_mobile: 13, claude_backend: "json", claude_model: "", claude_effort: "" }, updated_at: data.updated_at };
        localStorage.setItem("token", data.token);
        localStorage.setItem("account", JSON.stringify(this.account));
        this.setToken(data.token);
    }

    async signIn(email: string, password: string): Promise<void> {
        await this._authRequest("account/sign-in/", { email, password });
    }

    async signUp(email: string, password: string, fullName: string): Promise<void> {
        await this._authRequest("account/register/", { email, password, full_name: fullName });
    }

    signOut() {
        this.account = null;
        localStorage.removeItem("token");
        localStorage.removeItem("account");
        this.setToken(null);
    }
}
